import { useCallback, useState } from 'react';

import mockupApi from '@repo/editor/src/api/mockups';
import { Mockup as APIMockup } from '@repo/editor/src/types/Mockup';

import { buildMockupUrl } from 'global/urlUtils';
import { IMAGE_PROXY_PROVIDER } from 'strapi/component/shared/LandingImage';

import { ImageInfo } from '../shared/types';

import { Mockup } from './types';

interface useMockupsGroupGridReturn {
  fetchMockups: (mockupsGroupId: number) => Promise<void>;
  mockups: Mockup[];
  isLoading: boolean;
}

const DEFAULT_PREVIEW_SIZE = 512; // same as PROXY_WIDTH and PROXY_HEIGHT in @repo/editor/src/utils/url
const MOCKUPS_AMOUNT_TO_SHOW = 12;

const previewUrlToImageInfo = (preview: string): ImageInfo => {
  const fileName = preview.split('/').pop()?.split('?')[0] ?? '';

  return {
    url: preview,
    alt: fileName,
    width: DEFAULT_PREVIEW_SIZE,
    height: DEFAULT_PREVIEW_SIZE,
    filename: fileName,
    provider: IMAGE_PROXY_PROVIDER,
  };
};

const APIResponseToMockups = (
  mockups: APIMockup[],
  mockupsGroupId: number
): Mockup[] =>
  mockups.map((mockup) => ({
    id: mockup.id,
    preview: previewUrlToImageInfo(mockup.preview),
    name: mockup.name ?? mockup.id.toString(),
    url: buildMockupUrl(mockupsGroupId, mockup.id),
  }));

const useMockupsGroupGrid = (): useMockupsGroupGridReturn => {
  const [mockups, setMockups] = useState<Mockup[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMockups = useCallback(async (mockupsGroupId: number) => {
    setIsLoading(true);
    const response = await mockupApi.getGroup(
      mockupsGroupId.toString(),
      MOCKUPS_AMOUNT_TO_SHOW,
      0,
      'popularity'
    );

    if (!response?.success) {
      setIsLoading(false);
      return;
    }

    setMockups(APIResponseToMockups(response.mockups, mockupsGroupId));
    setIsLoading(false);
  }, []);

  return {
    mockups,
    fetchMockups,
    isLoading,
  };
};

export default useMockupsGroupGrid;
